import { useEffect, useState } from 'react';
import s from './Avatar.module.css';

/**
 * Foto de perfil, com o bonequinho como estado padrão.
 *
 * Existe como componente porque o avatar aparece em quatro lugares — cabeçalho,
 * gaveta da conta, cartão de usuário e perfil — e cada um tinha o mesmo SVG
 * copiado. Sem isto, ligar a foto significaria repetir a mesma lógica de
 * fallback quatro vezes, e a quinta tela nasceria sem ela.
 *
 * O tamanho vem por prop e vira variável CSS: o SVG interno escala junto, então
 * quem usa não precisa saber que existe um SVG lá dentro.
 */
export function Avatar({
  src,
  nome,
  tamanho = 40,
  className,
}: {
  src?: string | null;
  /** Usado só como texto alternativo — o avatar é decorativo quando não há foto. */
  nome?: string | null;
  tamanho?: number;
  className?: string;
}) {
  // Se a imagem não carregar (arquivo apagado no servidor, sessão caída), o
  // bonequinho volta. Um <img> quebrado no lugar do rosto é pior que o padrão.
  const [falhou, setFalhou] = useState(false);

  // Trocar a foto muda a URL; sem isto o estado de falha da imagem anterior
  // sobreviveria à troca e a nova nunca apareceria.
  useEffect(() => {
    setFalhou(false);
  }, [src]);

  const estilo = { '--avatar-tamanho': `${String(tamanho)}px` } as React.CSSProperties;
  const classe = className ? `${s.raiz} ${className}` : s.raiz;

  if (src && !falhou) {
    return (
      <span className={classe} style={estilo}>
        <img
          className={s.foto}
          src={src}
          alt={nome ? `Foto de ${nome}` : 'Foto de perfil'}
          onError={() => {
            setFalhou(true);
          }}
        />
      </span>
    );
  }

  return (
    <span className={classe} style={estilo} aria-hidden="true">
      <svg className={s.silhueta} viewBox="0 0 40 40" fill="currentColor">
        <circle cx="20" cy="15" r="7.2" opacity=".85" />
        <path d="M20 24.5c7.2 0 13 4.6 13 10.3v5.2H7v-5.2c0-5.7 5.8-10.3 13-10.3z" opacity=".85" />
      </svg>
    </span>
  );
}
